import React from "react";

const RESUME = "/assets/RusenEmreSabazResume.pdf";
const GITHUB = "https://github.com/EmreSab";
const LINKEDIN = "https://www.linkedin.com/in/rusen-emre-sabaz-903938203/";
const EMAIL = "mailto:emresbz@gmail.com";

const TECH = [
  { name: "AWS", icon: "/assets/svgs/aws.svg" },
  { name: "C#", icon: "/assets/svgs/csharp.svg" },
  { name: "CSS3", icon: "/assets/svgs/css3.svg" },
  { name: "Figma", icon: "/assets/svgs/figma.svg" },
  { name: "Git", icon: "/assets/svgs/git.svg" },
  { name: "HTML5", icon: "/assets/svgs/html5.svg" },
  { name: "Java", icon: "/assets/svgs/java.svg" },
  { name: "Jira", icon: "/assets/svgs/jira.svg" },
  { name: "Node.js", icon: "/assets/svgs/nodejs.svg" },
  { name: "OpenCV", icon: "/assets/svgs/opencv.svg" },
  { name: "PostgreSQL", icon: "/assets/svgs/postgresql.svg" },
  { name: "Python", icon: "/assets/svgs/python.svg" },
  { name: "PyTorch", icon: "/assets/svgs/pytorch.svg" },
  { name: "React", icon: "/assets/svgs/react.svg" },
];

const PROJECTS = [
  {
    name: "Hospital Work Management System",
    slug: "hospital-mgmt — java",
    description:
      "The BWH Hospital Management App enhances hospital efficiency with a Service Request Management System for staff and administrators, an internal navigation tool for guiding visitors, and integrated alerts for seamless communication.",
    long: "Developed over 7 weeks by a team of 10 following Scrum practices in an Agile environment. I served as the Back End Database Engineer, designing and managing the PostgreSQL database behind the service request management system and an internal hospital navigation tool with a Google-Maps-like interface for guiding visitors — all seamlessly integrated with our Java/JavaFX front end. Daily stand-ups and sprint reviews kept the team aligned throughout.",
    backgroundImage: "/assets/bringam.jpg",
    techStack: ["Java", "JavaFX", "PostgreSQL"],
    images: ["/assets/class-diagrams.jpg"],
    links: [
      { label: "view source on github", href: "https://github.com/EmreSab/SoftEng-BrighamWomensApp" },
    ],
  },
  {
    name: "Investment App",
    slug: "stockade — react-native",
    description:
      "StockADE is a React-based mobile app developed with Expo CLI to engage Gen Z and Millennials in financial practices, offering real-time stock data and a swipe interface for portfolio management on iOS and Android.",
    long: "StockADE was built by a team of 3 (1 business + 2 CS students) to engage Gen Z and Millennials with financial practices and stock portfolio management. Built with React and Expo CLI, it supports both iOS and Android from a single codebase. In collaboration with Fidelity Center for Applied Technology (FCAT), the app pulls real-time financial data through their API — with a swipe interface: left to pass, right to watchlist, up to buy/sell.",
    backgroundImage: "/assets/stock.jpg",
    techStack: ["React", "Expo CLI", "Node.js"],
    images: ["/assets/stockade-1.png", "/assets/stockade-2.png"],
    links: [{ label: "final paper (pdf)", href: "/assets/docs/stockade-fcat-paper.pdf" }],
  },
  {
    name: "Class Scheduler",
    slug: "class-scheduler — python",
    description:
      "Streamlines course planning by aggregating class difficulty, professor ratings, and workload estimates through web scraping Workday, RateMyProfessor, and OSCAR using image processing.",
    long: "The WPI Course Schedule Optimization Tool aggregates class difficulty, professor ratings, and course details into a unified database by web scraping Workday and RateMyProfessor, and using image processing to scrape elements from the OSCAR site. An extension of an existing MQP project, it enhances course planning with workload estimates and professor ratings — a streamlined, visually intuitive interface for planning schedules and fulfilling degree requirements.",
    backgroundImage: "/assets/CourseScheduler.jpg",
    techStack: ["Python", "Node.js", "OpenCV"],
    images: ["/assets/workflow.png", "/assets/image-processing.png"],
    links: [
      { label: "final presentation (pdf)", href: "/assets/docs/scheduler-presentation.pdf" },
      { label: "view source on github", href: "https://github.com/amicarellade/WPI-Student-Course-Optimization-23-24" },
    ],
  },
  {
    name: "Audio Style Transfer",
    slug: "audio-style-transfer — pytorch",
    description:
      "Audio style transfer techniques to modify the accent in human speech without altering its original contents.",
    long: "Applies audio style transfer (AST) techniques to modify the accent in human speech without altering its original content. Neural style transfer is integrated into a text-to-speech pipeline: accent features captured by a pre-trained VGG19 model are combined with content representations from Tacotron and a speaker-conditional WaveRNN vocoder for zero-shot adaptation to new speakers. The system preserves linguistic meaning while embedding distinct accent characteristics, verified through mel-spectrogram analysis.",
    backgroundImage: "/assets/CS541.jpg",
    techStack: ["Python", "PyTorch"],
    images: ["/assets/our-addition.png"],
    links: [
      { label: "final report (pdf)", href: "/assets/docs/ast-final-report.pdf" },
      { label: "view source on github", href: "https://github.com/amicarellade/CS541-ast" },
    ],
  },
];

const COMPANIES = [
  {
    name: "Mitsubishi Electric Iconics Digital Solutions",
    website: "https://iconics.com/en-us/",
    tenure: "Full-time · 1 yr 3 mos",
    location: "Foxborough, Massachusetts, United States · Hybrid",
    img: "/assets/ICONICS_logo.png",
    roles: [
      { title: "Software Development Engineer", badge: "current", duration: "Dec 2025 – Present · 8 mos", bullets: [] },
      { title: "Quality Assurance Engineer", badge: "", duration: "May 2025 – Dec 2025 · 8 mos", bullets: [] },
      {
        title: "Software Quality Assurance Intern",
        badge: "internship",
        duration: "Jun 2024 – Sep 2024 · 4 mos",
        bullets: [
          "Designed and executed automated tests for .NET applications, including verifying data manipulations and automating workflows using MSTest.",
          "Developed and tested REST APIs for authentication and authorization (OAuth2, PKCE), including the integration of a test server within company systems for comprehensive testing.",
        ],
      },
    ],
  },
  {
    name: "Enerjisa",
    website: "https://www.enerjisa.com.tr/en/home",
    tenure: "Internship · 2 mos",
    location: "Istanbul, Türkiye · On-site",
    img: "/assets/enerjisa.png",
    roles: [
      {
        title: "System Engineering Intern",
        badge: "internship",
        duration: "Jun 2023 – Jul 2023 · 2 mos",
        bullets: [
          "Led the setup of an Active Directory and Exchange Server.",
          "Managed user permissions and account setups.",
          "Integrated software solutions with existing IT infrastructure and provided technical support.",
        ],
      },
    ],
  },
];

const WIN_META = [
  ["hero", "abt", "about — whoami"],
  ["experience", "exp", "experience.log"],
  ["projects", "prj", "~/projects"],
  ["contact", "ctc", "contact.sh"],
];

function TitleBar({ title, right, onClose, onMin, onZoom }) {
  return (
    <div className="titlebar">
      <span className="tdot r" title="close" onClick={onClose} />
      <span className="tdot y" title="minimize" onClick={onMin} />
      <span className="tdot g" title="restore" onClick={onZoom} />
      <span className="ttitle">{title}</span>
      {right && <span className="tright">{right}</span>}
    </div>
  );
}

class DesktopPortfolio extends React.Component {
  state = {
    now: new Date(),
    win: { hero: "open", experience: "open", projects: "open", contact: "open" },
    modal: null,
    termOn: false,
    termVal: "",
    termLines: [{ text: "portfolio shell v1.0 — type 'help' to get started", color: "#78716c" }],
  };

  termBodyRef = React.createRef();
  termInputRef = React.createRef();

  componentDidMount() {
    this._checkReveal = () => this.checkReveal();
    window.addEventListener("scroll", this._checkReveal, { passive: true });
    window.addEventListener("resize", this._checkReveal);
    this._onKey = (e) => {
      if (e.key === "Escape") {
        if (this.state.modal !== null) this.setState({ modal: null });
        else if (this.state.termOn) this.setState({ termOn: false });
      }
    };
    window.addEventListener("keydown", this._onKey);
    this.setupReveal();
    this._revealTimer = setInterval(() => this.setupReveal(), 450);
    this._clock = setInterval(() => this.setState({ now: new Date() }), 20000);
  }

  componentDidUpdate() {
    this.setupReveal();
    const b = this.termBodyRef.current;
    if (b) b.scrollTop = b.scrollHeight;
  }

  componentWillUnmount() {
    clearInterval(this._revealTimer);
    clearInterval(this._clock);
    window.removeEventListener("scroll", this._checkReveal);
    window.removeEventListener("resize", this._checkReveal);
    window.removeEventListener("keydown", this._onKey);
  }

  setupReveal() {
    document.querySelectorAll("[data-reveal]").forEach((el) => {
      if (el.dataset.revealBound) return;
      el.dataset.revealBound = "1";
      const r = el.getBoundingClientRect();
      const inView = r.top < window.innerHeight * 0.95 && r.bottom > 0;
      if (inView) {
        el.dataset.revealed = "1";
      } else {
        el.style.transition = "opacity 0.7s ease, transform 0.7s cubic-bezier(0.16,1,0.3,1)";
        el.style.opacity = "0";
        el.style.transform = "translateY(28px)";
      }
    });
    this.checkReveal();
  }

  checkReveal() {
    document.querySelectorAll("[data-reveal]").forEach((el) => {
      if (!el.dataset.revealBound || el.dataset.revealed) return;
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight * 0.92 && r.bottom > 0) {
        el.dataset.revealed = "1";
        el.style.opacity = "1";
        el.style.transform = "translateY(0)";
      }
    });
  }

  scrollTo(id) {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 52;
    window.scrollTo({ top, behavior: "smooth" });
  }

  setWin(k, v) {
    this.setState((s) => ({ win: { ...s.win, [k]: v } }));
  }

  goSection(winKey, id) {
    if (this.state.win[winKey] !== "open") this.setWin(winKey, "open");
    setTimeout(() => this.scrollTo(id), 80);
  }

  goTop = () => {
    this.setWin("hero", "open");
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 60);
  };
  goExperience = () => this.goSection("experience", "experience");
  goProjects = () => this.goSection("projects", "projects");
  goContact = () => this.goSection("contact", "contact");

  toggleTerm = () => {
    const opening = !this.state.termOn;
    this.setState({ termOn: opening });
    if (opening)
      setTimeout(() => {
        if (this.termInputRef.current) this.termInputRef.current.focus();
      }, 80);
  };

  runCmd(raw) {
    const out = [{ text: "➜ ~ " + raw, color: "#78716c" }];
    const parts = raw.trim().split(/\s+/);
    const cmd = (parts[0] || "").toLowerCase();
    const arg = (parts[1] || "").toLowerCase();
    const say = (text, color) => out.push({ text, color: color || "#d6d3d1" });

    const openSection = (name) => {
      if (name === "about" || name === "about.md") {
        this.setWin("hero", "open");
        setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 80);
        say("opening about.md ...");
      } else if (name.startsWith("exp")) {
        this.goSection("experience", "experience");
        say("opening experience.log ...");
      } else if (name.startsWith("proj")) {
        this.goSection("projects", "projects");
        say("opening ~/projects ...");
      } else if (name.startsWith("cont")) {
        this.goSection("contact", "contact");
        say("opening contact.sh ...");
      } else if (name.startsWith("resume")) {
        window.open(RESUME, "_blank");
        say("opening resume.pdf ...");
      } else if (["1", "2", "3", "4"].includes(name)) {
        this.setState({ modal: Number(name) - 1 });
        say("opening project " + name + " ...");
      } else say("open: no such file or directory: " + (name || ""), "#f87171");
    };

    if (cmd === "") {
      /* empty */
    } else if (cmd === "help") {
      say("available commands:", "#fbbf24");
      say("  help              show this list");
      say("  whoami            about me");
      say("  ls                list files");
      say("  ls projects       list projects");
      say("  open <name|1-4>   open a section or project");
      say("  cat about.md      print bio");
      say("  neofetch          system info");
      say("  resume            open resume.pdf");
      say("  github / linkedin / email");
      say("  sudo hire-me      (try it)");
      say("  clear             clear terminal");
    } else if (cmd === "whoami") {
      say("Rusen Emre Sabaz — Software Development Engineer @ Mitsubishi Electric Iconics Digital Solutions");
    } else if (cmd === "ls" && (arg === "projects" || arg === "projects/")) {
      say("1. hospital-mgmt      2. stockade");
      say("3. class-scheduler    4. audio-style-transfer");
      say("hint: open 1", "#78716c");
    } else if (cmd === "ls") {
      say("about.md   experience.log   projects/   contact.sh   resume.pdf");
    } else if (cmd === "cat" && (arg === "about.md" || arg === "about")) {
      say("M.S. Computer Science at Worcester Polytechnic Institute — experience across software development, machine learning, and quality assurance. Proficient in Python, React, .NET, and C#.");
    } else if (cmd === "cat") {
      say("cat: " + (arg || "") + ": no such file", "#f87171");
    } else if (cmd === "open") {
      openSection(arg);
    } else if (cmd === "projects") {
      openSection("projects");
    } else if (cmd === "experience") {
      openSection("experience");
    } else if (cmd === "contact") {
      openSection("contact");
    } else if (cmd === "resume") {
      window.open(RESUME, "_blank");
      say("opening resume.pdf ...");
    } else if (cmd === "github") {
      window.open(GITHUB, "_blank");
      say("opening github.com/EmreSab ...");
    } else if (cmd === "linkedin") {
      window.open(LINKEDIN, "_blank");
      say("opening linkedin ...");
    } else if (cmd === "email") {
      window.open(EMAIL);
      say("opening mail client ...");
    } else if (cmd === "neofetch") {
      say("        ⬢          emre@portfolio", "#fbbf24");
      say("      ⬢ ⬢ ⬢        ──────────────", "#fbbf24");
      say("        ⬢          role:   Software Development Engineer", "#d6d3d1");
      say("                   edu:    M.S. Computer Science @ WPI");
      say("                   loc:    Massachusetts, USA");
      say("                   stack:  Python · React · .NET · C#");
      say("                   shell:  guest-mode (no sudo... or is there?)", "#78716c");
    } else if (cmd === "sudo" && raw.toLowerCase().includes("hire")) {
      say("[sudo] password for guest: ********", "#78716c");
      say("access granted ✓", "#4ade80");
      say("opening mail client with subject 'Let's talk' ...");
      setTimeout(() => window.open(EMAIL + "?subject=Let's%20talk"), 700);
    } else if (cmd === "sudo") {
      say("guest is not in the sudoers file. this incident will be reported.", "#f87171");
    } else if (cmd === "clear") {
      this.setState({ termLines: [], termVal: "" });
      return;
    } else if (cmd === "date") {
      say(new Date().toString());
    } else if (cmd === "exit") {
      this.setState({ termOn: false, termVal: "" });
      return;
    } else {
      say("zsh: command not found: " + cmd + "  (try 'help')", "#f87171");
    }
    this.setState((s) => ({ termLines: [...s.termLines, ...out], termVal: "" }));
  }

  win(k) {
    return {
      shown: this.state.win[k] !== "closed",
      body: this.state.win[k] === "open",
      close: () => this.setWin(k, "closed"),
      min: () => this.setWin(k, this.state.win[k] === "min" ? "open" : "min"),
      zoom: () => this.setWin(k, "open"),
    };
  }

  render() {
    const n = this.state.now;
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    let h = n.getHours();
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    const clock =
      days[n.getDay()] + " " + months[n.getMonth()] + " " + n.getDate() + "  " + h + ":" + String(n.getMinutes()).padStart(2, "0") + " " + ampm;

    const heroW = this.win("hero");
    const expW = this.win("experience");
    const projW = this.win("projects");
    const ctW = this.win("contact");

    const closedWins = WIN_META.filter(([k]) => this.state.win[k] === "closed").map(([k, label, title]) => ({
      label,
      title: "reopen " + title,
      onClick: () => this.setWin(k, "open"),
    }));

    const modal = this.state.modal !== null ? PROJECTS[this.state.modal] : null;

    return (
      <div className="dp">
        {/* wallpaper */}
        <div aria-hidden="true" className="wallpaper">
          <div className="orb1" />
          <div className="orb2" />
          <div className="walldots" />
        </div>

        {/* menu bar */}
        <nav className="menubar">
          <div className="menubar-left">
            <div className="brand" onClick={this.goTop}>⬢ emre.sabaz</div>
            <div className="mlink active" onClick={this.goTop}>About</div>
            <div className="mlink" onClick={this.goExperience}>Experience</div>
            <div className="mlink" onClick={this.goProjects}>Projects</div>
            <div className="mlink" onClick={this.goContact}>Contact</div>
          </div>
          <div className="menubar-right">
            <a className="resume-link" href={RESUME} target="_blank" rel="noreferrer">resume.pdf ↗</a>
            <span>{clock}</span>
          </div>
        </nav>

        {/* desktop icons */}
        <div className="deskicons">
          <a className="dicon" href={RESUME} target="_blank" rel="noreferrer">
            <div className="dicon-box pdf">PDF</div>
            <span className="dicon-label">resume.pdf</span>
          </a>
          <a className="dicon" href={GITHUB} target="_blank" rel="noreferrer">
            <div className="dicon-box"><img src="/assets/githubIcon.png" alt="GitHub" style={{ width: 22, height: 22 }} /></div>
            <span className="dicon-label">github/</span>
          </a>
          <div className="dicon" onClick={this.toggleTerm}>
            <div className="dicon-box">&gt;_</div>
            <span className="dicon-label">terminal</span>
          </div>
        </div>

        {/* hero terminal window */}
        <header className="section-wrap" style={{ padding: "92px 28px 30px" }}>
          {heroW.shown && (
            <div className="window hero-window">
              <TitleBar title="emre@desktop: ~/whoami" onClose={heroW.close} onMin={heroW.min} onZoom={heroW.zoom} />
              {heroW.body && (
                <div className="hero-grid">
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <div className="prompt-line">
                      <span className="prompt-arrow">➜</span> <span className="prompt-tilde">~</span> whoami
                    </div>
                    <h1 className="hero-name">Rusen Emre Sabaz<span style={{ color: "var(--ac)" }}>_</span></h1>
                    <div className="hero-role">
                      Software Development Engineer <span style={{ color: "#78716c" }}>@ Mitsubishi Electric Iconics</span>
                    </div>
                    <p className="hero-bio">
                      M.S. Computer Science at Worcester Polytechnic Institute — experience across software development,
                      machine learning, and quality assurance. Proficient in Python, React, .NET, and C#.
                    </p>
                    <div className="prompt-line" onClick={this.toggleTerm} title="open terminal" style={{ cursor: "pointer" }}>
                      <span className="prompt-arrow">➜</span> <span className="prompt-tilde">~</span>{" "}
                      <span className="cursor-block" /> <span style={{ opacity: 0.55 }}>click to open terminal</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 4, flexWrap: "wrap" }}>
                      <div className="btn-primary" onClick={this.goProjects}>./view-projects</div>
                      <div className="btn-ghost" onClick={this.goContact}>./contact</div>
                      <div style={{ display: "flex", gap: 9, marginLeft: 6 }}>
                        <a className="social-btn" href={GITHUB} target="_blank" rel="noreferrer">
                          <img src="/assets/githubIcon.png" alt="GitHub" style={{ width: 19, height: 19 }} />
                        </a>
                        <a className="social-btn" href={LINKEDIN} target="_blank" rel="noreferrer">
                          <img src="/assets/linkedinIcon.png" alt="LinkedIn" style={{ width: 19, height: 19 }} />
                        </a>
                        <a className="social-btn" href={EMAIL}>
                          <img src="/assets/emailIcon.png" alt="Email" style={{ width: 19, height: 19 }} />
                        </a>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
                    <div className="photo-frame">
                      <img src="/assets/ProfilePhoto.jpg" alt="Emre Sabaz" />
                    </div>
                    <div className="location-chip">
                      <span style={{ color: "var(--ac)" }}>📍</span> Massachusetts, USA
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </header>

        {/* tech ticker */}
        <div aria-hidden="true" className="ticker-wrap">
          <div className="ticker-mask">
            <div className="ticker-track">
              {TECH.concat(TECH).map((t, i) => (
                <div className="ticker-item" key={i}>
                  <img src={t.icon} alt={t.name} />
                  <span>{t.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* experience window */}
        <section id="experience" className="section-wrap">
          {expW.shown && (
            <div className="window" data-reveal="">
              <TitleBar title="experience.log" right={COMPANIES.length + " entries"} onClose={expW.close} onMin={expW.min} onZoom={expW.zoom} />
              {expW.body && (
                <div style={{ display: "flex", flexDirection: "column", gap: 18, padding: 28 }}>
                  {COMPANIES.map((company) => (
                    <div className="exp-card" key={company.name}>
                      <a className="exp-logo" href={company.website} target="_blank" rel="noreferrer">
                        <img src={company.img} alt={company.name} />
                      </a>
                      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
                          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{company.name}</h3>
                          <span className="mono" style={{ fontSize: 11.5, color: "#78716c" }}>{company.tenure}</span>
                        </div>
                        <div style={{ fontSize: 13, color: "#78716c" }}>{company.location}</div>
                        <div className="roles">
                          {company.roles.map((role) => (
                            <div className="role" key={role.title}>
                              <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                                <span style={{ fontSize: 15, fontWeight: 600, color: "#e7e5e4" }}>{role.title}</span>
                                {role.badge && <span className="mono" style={{ fontSize: 11, color: "var(--ac)" }}>{role.badge}</span>}
                              </div>
                              <span className="mono" style={{ fontSize: 11.5, color: "#78716c" }}>{role.duration}</span>
                              {role.bullets.length > 0 && (
                                <ul className="role-bullets">
                                  {role.bullets.map((b, i) => (
                                    <li key={i}>{b}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        {/* projects window */}
        <section id="projects" className="section-wrap">
          {projW.shown && (
            <div className="window" data-reveal="">
              <TitleBar title="~/projects" right={PROJECTS.length + " items · click to open"} onClose={projW.close} onMin={projW.min} onZoom={projW.zoom} />
              {projW.body && (
                <div className="proj-grid">
                  {PROJECTS.map((p, i) => (
                    <div className="pcard" key={p.slug} onClick={() => this.setState({ modal: i })}>
                      <div className="pcard-bar">
                        <span className="pdot" style={{ background: "#f87171" }} />
                        <span className="pdot" style={{ background: "#fbbf24" }} />
                        <span className="pdot" style={{ background: "#4ade80" }} />
                        <span className="pcard-slug">{p.slug}</span>
                        <span className="openhint">open ↗</span>
                      </div>
                      <div className="pimg-clip">
                        <div className="pimg" style={{ backgroundImage: `url(${p.backgroundImage})` }} />
                      </div>
                      <div className="pcard-body">
                        <h3><span style={{ color: "var(--ac)" }}>$</span> {p.name}</h3>
                        <p>{p.description}</p>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginTop: 3 }}>
                          {p.techStack.map((t) => (
                            <span className="ptag" key={t}>[{t}]</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        {/* contact window */}
        <section id="contact" className="section-wrap" style={{ maxWidth: 760, padding: "26px 28px 40px" }}>
          {ctW.shown && (
            <div className="window" data-reveal="" style={{ background: "rgba(18,16,13,0.94)" }}>
              <TitleBar title="contact.sh" onClose={ctW.close} onMin={ctW.min} onZoom={ctW.zoom} />
              {ctW.body && (
                <div className="contact-body">
                  <div className="contact-glow" />
                  <div className="prompt-line" style={{ fontSize: 13 }}>
                    <span className="prompt-arrow">➜</span> <span className="prompt-tilde">~</span> ./contact.sh --hire-me
                  </div>
                  <h2 className="contact-title">Let's build something<span style={{ color: "var(--ac)" }}>.</span></h2>
                  <p className="contact-text">
                    Open to freelance work and full-time opportunities. The fastest way to reach me is email — I usually
                    reply within a day.
                  </p>
                  <a className="email-btn" href={EMAIL}>emresbz@gmail.com</a>
                  <div style={{ display: "flex", gap: 11, marginTop: 4 }}>
                    <a className="social-btn lg" href={GITHUB} target="_blank" rel="noreferrer">
                      <img src="/assets/githubIcon.png" alt="GitHub" style={{ width: 19, height: 19 }} />
                    </a>
                    <a className="social-btn lg" href={LINKEDIN} target="_blank" rel="noreferrer">
                      <img src="/assets/linkedinIcon.png" alt="LinkedIn" style={{ width: 19, height: 19 }} />
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* footer */}
        <footer>
          © 2026 emre sabaz · built with <span style={{ color: "var(--ac)" }}>react</span> · tip: open the{" "}
          <span className="footer-accent" onClick={this.toggleTerm}>terminal</span> and type 'help'
        </footer>

        {/* interactive terminal */}
        {this.state.termOn && (
          <div className="term">
            <div className="titlebar">
              <span className="tdot r" title="close" onClick={this.toggleTerm} />
              <span className="tdot y" title="minimize" onClick={this.toggleTerm} />
              <span className="tdot g static" />
              <span className="ttitle" style={{ marginLeft: 10, fontSize: 12 }}>guest@portfolio: ~</span>
            </div>
            <div className="term-body" ref={this.termBodyRef}>
              {this.state.termLines.map((line, i) => (
                <div className="term-line" key={i} style={{ color: line.color }}>{line.text}</div>
              ))}
              <div className="term-input-row">
                <span style={{ color: "#4ade80" }}>➜</span>
                <span style={{ color: "var(--ac)" }}>~</span>
                <input
                  ref={this.termInputRef}
                  value={this.state.termVal}
                  onChange={(e) => this.setState({ termVal: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") this.runCmd(e.target.value); }}
                  spellCheck={false}
                  autoComplete="off"
                  aria-label="terminal input"
                />
              </div>
            </div>
          </div>
        )}

        {/* project detail modal */}
        {modal && (
          <div className="modal-overlay" onClick={() => this.setState({ modal: null })}>
            <div className="modal-win" onClick={(e) => e.stopPropagation()}>
              <div className="titlebar">
                <span className="tdot r" title="close" onClick={() => this.setState({ modal: null })} />
                <span className="tdot y" title="minimize" onClick={() => this.setState({ modal: null })} />
                <span className="tdot g static" />
                <span className="ttitle">{modal.slug}</span>
                <span className="modal-esc" onClick={() => this.setState({ modal: null })}>esc ✕</span>
              </div>
              <div className="modal-body">
                <h3><span style={{ color: "var(--ac)" }}>$</span> {modal.name}</h3>
                <p className="long">{modal.long}</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {modal.techStack.map((t) => (
                    <span className="modal-tag" key={t}>[{t}]</span>
                  ))}
                </div>
                <div className="modal-images">
                  {modal.images.map((im) => (
                    <img src={im} alt={modal.name} key={im} />
                  ))}
                </div>
                <div className="modal-links">
                  <span className="mono" style={{ fontSize: 11.5, color: "#57534e" }}>resources/</span>
                  {modal.links.map((lnk) => (
                    <a href={lnk.href} target="_blank" rel="noreferrer" key={lnk.href}>→ {lnk.label}</a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* dock */}
        <div className="dock">
          <a className="dock-item" href={GITHUB} target="_blank" rel="noreferrer" title="GitHub">
            <img src="/assets/githubIcon.png" alt="GitHub" />
          </a>
          <a className="dock-item" href={LINKEDIN} target="_blank" rel="noreferrer" title="LinkedIn">
            <img src="/assets/linkedinIcon.png" alt="LinkedIn" />
          </a>
          <a className="dock-item" href={EMAIL} title="Email">
            <img src="/assets/emailIcon.png" alt="Email" />
          </a>
          <div className="dock-item term-btn" onClick={this.toggleTerm} title="Terminal — type 'help'">&gt;_</div>
          <div className="dock-sep" />
          <a className="dock-cv" href={RESUME} target="_blank" rel="noreferrer" title="Resume">CV</a>
          {closedWins.length > 0 && (
            <>
              <div className="dock-sep" />
              {closedWins.map((w) => (
                <div className="dock-restore" key={w.label} onClick={w.onClick} title={w.title}>{w.label} ↺</div>
              ))}
            </>
          )}
        </div>
      </div>
    );
  }
}

export default DesktopPortfolio;
